import styles from './button.module.css';
import { cx } from './cx';

/**
 * The button's class list, without the component.
 *
 * It has its own file because half the app's button-shaped things are **links** — §8's jump-out row,
 * "Back to the start" — and a link must stay an `<a href>` so that middle-click, ⌘-click and "copy link
 * address" keep working (`router/Link.tsx` says the same about the inverse mistake). One class function,
 * two elements, rather than a `<Button as="a">` that has to re-implement anchor semantics.
 *
 * Separate from `Button.tsx` rather than exported beside it so that file exports components only, which
 * is what Vite's fast refresh needs to swap a component without reloading the page.
 */

export type ButtonVariant = 'primary' | 'default' | 'subtle' | 'ghost' | 'pill' | 'media' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonClassOptions {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Icon-only: square, and the caller must supply an accessible name. */
  readonly icon?: boolean;
  readonly className?: string;
}

export function buttonClass({
  variant = 'default',
  size = 'md',
  icon = false,
  className,
}: ButtonClassOptions = {}): string {
  return cx(
    styles.button,
    styles[size],
    styles[variant],
    // `subtle` is text in a sentence: giving it a 36 px square would put a hole in the paragraph.
    icon && variant !== 'subtle' && styles.icon,
    icon && size === 'sm' && styles.iconSm,
    className,
  );
}
